// engine/sprites.ts — procedural pixel-art sprites.
// A painter draws on an integer art grid; the bake pass adds a 1px dark outline,
// then blits to an offscreen canvas at 2x with smoothing off. Zero image assets.
//
// Recipes (SpriteRecipe from types.ts) are registered by data/content under an id
// and baked once per palette at boot / zone entry.

import type { SpriteRecipe, ZonePalette } from '../types'

export const BAKE = 2

// ---------------------------------------------------------------- color utils

export type RGBA = [number, number, number, number]

const colorCache = new Map<string, RGBA>()

export function parseColor(c: string): RGBA {
  const hit = colorCache.get(c)
  if (hit) return hit
  let out: RGBA = [255, 0, 255, 255]
  const s = c.trim()
  if (s.startsWith('#')) {
    const h = s.slice(1)
    if (h.length === 3) {
      out = [
        parseInt(h[0] + h[0], 16),
        parseInt(h[1] + h[1], 16),
        parseInt(h[2] + h[2], 16),
        255,
      ]
    } else if (h.length === 6 || h.length === 8) {
      out = [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
        h.length === 8 ? parseInt(h.slice(6, 8), 16) : 255,
      ]
    }
  }
  colorCache.set(c, out)
  return out
}

function hex2(n: number): string {
  const v = Math.max(0, Math.min(255, Math.round(n)))
  return v.toString(16).padStart(2, '0')
}

export function toHex(r: number, g: number, b: number): string {
  return '#' + hex2(r) + hex2(g) + hex2(b)
}

/** amt > 0 lightens toward white, amt < 0 darkens toward black. */
export function shade(color: string, amt: number): string {
  const [r, g, b] = parseColor(color)
  if (amt >= 0) {
    return toHex(r + (255 - r) * amt, g + (255 - g) * amt, b + (255 - b) * amt)
  }
  const k = 1 + amt
  return toHex(r * k, g * k, b * k)
}

export function mix(a: string, b: string, t: number): string {
  const A = parseColor(a)
  const B = parseColor(b)
  return toHex(A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t)
}

// ---------------------------------------------------------------- pixel canvas

export class PixelArt {
  readonly size: number
  readonly data: Uint8ClampedArray

  constructor(size: number) {
    this.size = size
    this.data = new Uint8ClampedArray(size * size * 4)
  }

  set(x: number, y: number, color: string, alpha = 1): void {
    x |= 0
    y |= 0
    if (x < 0 || y < 0 || x >= this.size || y >= this.size) return
    const [r, g, b, a] = parseColor(color)
    const i = (y * this.size + x) * 4
    const av = a * alpha
    if (av >= 255) {
      this.data[i] = r
      this.data[i + 1] = g
      this.data[i + 2] = b
      this.data[i + 3] = 255
      return
    }
    // simple source-over
    const sa = av / 255
    const da = this.data[i + 3] / 255
    const oa = sa + da * (1 - sa)
    if (oa <= 0) return
    this.data[i] = (r * sa + this.data[i] * da * (1 - sa)) / oa
    this.data[i + 1] = (g * sa + this.data[i + 1] * da * (1 - sa)) / oa
    this.data[i + 2] = (b * sa + this.data[i + 2] * da * (1 - sa)) / oa
    this.data[i + 3] = oa * 255
  }

  opaqueAt(x: number, y: number): boolean {
    if (x < 0 || y < 0 || x >= this.size || y >= this.size) return false
    return this.data[(y * this.size + x) * 4 + 3] > 8
  }

  rect(x: number, y: number, w: number, h: number, color: string, alpha = 1): void {
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) this.set(x + i, y + j, color, alpha)
  }

  hline(x: number, y: number, w: number, color: string, alpha = 1): void {
    for (let i = 0; i < w; i++) this.set(x + i, y, color, alpha)
  }

  vline(x: number, y: number, h: number, color: string, alpha = 1): void {
    for (let j = 0; j < h; j++) this.set(x, y + j, color, alpha)
  }

  line(x0: number, y0: number, x1: number, y1: number, color: string): void {
    let x = x0 | 0
    let y = y0 | 0
    const dx = Math.abs((x1 | 0) - x)
    const dy = -Math.abs((y1 | 0) - y)
    const sx = x < x1 ? 1 : -1
    const sy = y < y1 ? 1 : -1
    let err = dx + dy
    for (let guard = 0; guard < 512; guard++) {
      this.set(x, y, color)
      if (x === (x1 | 0) && y === (y1 | 0)) break
      const e2 = 2 * err
      if (e2 >= dy) {
        err += dy
        x += sx
      }
      if (e2 <= dx) {
        err += dx
        y += sy
      }
    }
  }

  /** Filled axis-aligned ellipse; the workhorse for organic pixel bodies. */
  ellipse(cx: number, cy: number, rx: number, ry: number, color: string, alpha = 1): void {
    if (rx <= 0 || ry <= 0) return
    const x0 = Math.floor(cx - rx)
    const x1 = Math.ceil(cx + rx)
    const y0 = Math.floor(cy - ry)
    const y1 = Math.ceil(cy + ry)
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const nx = (x + 0.5 - cx) / rx
        const ny = (y + 0.5 - cy) / ry
        if (nx * nx + ny * ny <= 1) this.set(x, y, color, alpha)
      }
    }
  }

  disc(cx: number, cy: number, r: number, color: string, alpha = 1): void {
    this.ellipse(cx, cy, r, r, color, alpha)
  }

  ring(cx: number, cy: number, r: number, color: string): void {
    const steps = Math.max(12, Math.round(r * 8))
    for (let i = 0; i < steps; i++) {
      const a = (i / steps) * Math.PI * 2
      this.set(Math.round(cx + Math.cos(a) * r), Math.round(cy + Math.sin(a) * r), color)
    }
  }

  /** Mirror the left half onto the right half — keeps bodies symmetrical for free. */
  mirrorX(): void {
    const s = this.size
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s >> 1; x++) {
        const a = (y * s + x) * 4
        const b = (y * s + (s - 1 - x)) * 4
        this.data[b] = this.data[a]
        this.data[b + 1] = this.data[a + 1]
        this.data[b + 2] = this.data[a + 2]
        this.data[b + 3] = this.data[a + 3]
      }
    }
  }

  /** Dark 1px border around everything opaque. The "chunky 1-bit-plus" look. */
  outline(color: string): void {
    const s = this.size
    const [r, g, b] = parseColor(color)
    const src = this.data.slice()
    const op = (x: number, y: number): boolean =>
      x >= 0 && y >= 0 && x < s && y < s && src[(y * s + x) * 4 + 3] > 8
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s; x++) {
        const i = (y * s + x) * 4
        if (src[i + 3] > 8) continue
        if (op(x - 1, y) || op(x + 1, y) || op(x, y - 1) || op(x, y + 1)) {
          this.data[i] = r
          this.data[i + 1] = g
          this.data[i + 2] = b
          this.data[i + 3] = 255
        }
      }
    }
  }
}

// ---------------------------------------------------------------- registry

export type PainterParams = Record<string, number | string>
export type Painter = (a: PixelArt, p: PainterParams, pal: ZonePalette) => void

const painters = new Map<string, Painter>()

export function registerPainter(key: string, fn: Painter): void {
  painters.set(key, fn)
}

export function hasPainter(key: string): boolean {
  return painters.has(key)
}

interface Baked {
  canvas: HTMLCanvasElement
  white: HTMLCanvasElement
  size: number
  tints: Map<string, HTMLCanvasElement>
}

const recipes = new Map<string, SpriteRecipe>()
const baked = new Map<string, Baked>()

export function registerRecipe(id: string, recipe: SpriteRecipe): void {
  recipes.set(id, recipe)
}

export function recipeIds(): string[] {
  return [...recipes.keys()]
}

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  return c
}

function bakeOne(recipe: SpriteRecipe, pal: ZonePalette): Baked {
  const size = Math.max(4, Math.round(recipe.size))
  const art = new PixelArt(size)
  const painter = painters.get(recipe.key)
  if (painter) {
    painter(art, recipe.params ?? {}, pal)
  } else {
    // Missing painter is loud but not fatal: a magenta box says "wire me up".
    art.rect(1, 1, size - 2, size - 2, '#ff00ff')
  }
  art.outline(mix('#05060a', pal.bg, 0.35))

  const flat = makeCanvas(size, size)
  const fctx = flat.getContext('2d')!
  const img = fctx.createImageData(size, size)
  img.data.set(art.data)
  fctx.putImageData(img, 0, 0)

  const canvas = makeCanvas(size * BAKE, size * BAKE)
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(flat, 0, 0, size, size, 0, 0, size * BAKE, size * BAKE)

  const white = makeCanvas(size * BAKE, size * BAKE)
  const wctx = white.getContext('2d')!
  wctx.imageSmoothingEnabled = false
  wctx.drawImage(canvas, 0, 0)
  wctx.globalCompositeOperation = 'source-in'
  wctx.fillStyle = '#ffffff'
  wctx.fillRect(0, 0, white.width, white.height)

  return { canvas, white, size, tints: new Map() }
}

/** Bake (or re-bake) every registered recipe for this palette. Call at zone entry. */
export function bakeAll(pal: ZonePalette): void {
  baked.clear()
  for (const [id, recipe] of recipes) {
    try {
      baked.set(id, bakeOne(recipe, pal))
    } catch {
      /* one bad recipe must not take down boot */
    }
  }
}

export function spriteSize(id: string): number {
  return baked.get(id)?.size ?? 16
}

export function hasSprite(id: string): boolean {
  return baked.has(id)
}

function tintCanvas(b: Baked, color: string): HTMLCanvasElement {
  const hit = b.tints.get(color)
  if (hit) return hit
  const c = makeCanvas(b.canvas.width, b.canvas.height)
  const ctx = c.getContext('2d')!
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(b.canvas, 0, 0)
  ctx.globalCompositeOperation = 'source-atop'
  ctx.globalAlpha = 0.75
  ctx.fillStyle = color
  ctx.fillRect(0, 0, c.width, c.height)
  b.tints.set(color, c)
  return c
}

export interface SpriteOpts {
  rot?: number
  scale?: number
  alpha?: number
  tint?: string
  flip?: boolean
  /** 0..1 white hit-flash overlay */
  white?: number
}

/**
 * Draw a baked sprite centred on (x, y) in the current transform's units.
 * No allocation. Positions snap to whole pixels when unrotated.
 */
export function drawSprite(
  ctx: CanvasRenderingContext2D,
  id: string,
  x: number,
  y: number,
  opts?: SpriteOpts,
): void {
  const b = baked.get(id)
  if (!b) return
  const scale = opts?.scale ?? 1
  const w = b.size * scale
  const h = b.size * scale
  const alpha = opts?.alpha ?? 1
  const rot = opts?.rot ?? 0
  const flip = opts?.flip === true
  const src = opts?.tint ? tintCanvas(b, opts.tint) : b.canvas

  if (alpha !== 1) ctx.globalAlpha = alpha

  if (rot === 0 && !flip) {
    const dx = Math.round(x - w / 2)
    const dy = Math.round(y - h / 2)
    ctx.drawImage(src, dx, dy, w, h)
    if (opts?.white) {
      ctx.globalAlpha = alpha * opts.white
      ctx.drawImage(b.white, dx, dy, w, h)
      ctx.globalAlpha = alpha
    }
  } else {
    ctx.save()
    ctx.translate(x, y)
    if (rot !== 0) ctx.rotate(rot)
    if (flip) ctx.scale(-1, 1)
    ctx.drawImage(src, -w / 2, -h / 2, w, h)
    if (opts?.white) {
      ctx.globalAlpha = alpha * opts.white
      ctx.drawImage(b.white, -w / 2, -h / 2, w, h)
    }
    ctx.restore()
  }

  if (alpha !== 1 || opts?.white) ctx.globalAlpha = 1
}

/** Direct access for the tile-layer compositor. */
export function spriteCanvas(id: string): HTMLCanvasElement | null {
  return baked.get(id)?.canvas ?? null
}

// ---------------------------------------------------------------- painters
// Every body is authored facing the viewer (south) with a 1px margin so the
// outline pass has room. Horizontal facing is done by flipping at draw time.

const INK = '#12101a'

function pget(p: PainterParams, k: string, d: number): number {
  const v = p[k]
  return typeof v === 'number' ? v : d
}
function pstr(p: PainterParams, k: string, d: string): string {
  const v = p[k]
  return typeof v === 'string' ? v : d
}

// --- floor / wall tiles -----------------------------------------------------

registerPainter('tile_floor', (a, p, pal) => {
  const base = pstr(p, 'color', pal.floor)
  const alt = pstr(p, 'alt', pal.floorAlt)
  const s = a.size
  a.rect(0, 0, s, s, base)
  // flagstone seams
  a.hline(0, 0, s, shade(base, -0.28))
  a.vline(0, 0, s, shade(base, -0.28))
  a.hline(0, s - 1, s, shade(base, 0.06))
  // speckle — deterministic, not random, so tiles tile cleanly
  for (let y = 2; y < s - 2; y += 5) {
    for (let x = 3; x < s - 2; x += 7) {
      a.set(x, y, shade(alt, -0.1))
      a.set(x + 1, y + 2, shade(base, -0.18))
    }
  }
})

registerPainter('tile_floor_alt', (a, p, pal) => {
  const base = pstr(p, 'color', pal.floorAlt)
  const s = a.size
  a.rect(0, 0, s, s, base)
  a.hline(0, 0, s, shade(base, -0.3))
  a.vline(0, 0, s, shade(base, -0.3))
  for (let y = 4; y < s - 2; y += 6) a.hline(2, y, s - 5, shade(base, -0.1))
})

registerPainter('tile_wall', (a, p, pal) => {
  const base = pstr(p, 'color', pal.wall)
  const edge = pstr(p, 'edge', pal.wallEdge)
  const s = a.size
  a.rect(0, 0, s, s, base)
  // brick courses
  for (let y = 0; y < s; y += 8) {
    a.hline(0, y, s, shade(base, -0.35))
    const off = (y / 8) % 2 === 0 ? 0 : 8
    for (let x = off; x < s; x += 16) a.vline(x, y, 8, shade(base, -0.35))
  }
  a.hline(0, 0, s, edge)
  a.hline(0, 1, s, shade(edge, -0.15))
  a.hline(0, s - 1, s, shade(base, -0.5))
})

// --- the Warden -------------------------------------------------------------

registerPainter('warden', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const base = pstr(p, 'cloak', '#4a5da0')
  const lo = shade(base, -0.34)
  const hi = shade(base, 0.18)
  const trim = pstr(p, 'trim', pal.accent)
  const ember = pal.glow

  // hem + cloak body: broad shoulders down to a flared hem
  a.ellipse(cx, s * 0.74, s * 0.33, s * 0.18, lo)
  a.rect(Math.round(cx - s * 0.28), Math.round(s * 0.44), Math.round(s * 0.56), Math.round(s * 0.3), base)
  a.ellipse(cx, s * 0.62, s * 0.31, s * 0.2, base)
  // fold lines
  a.vline(Math.round(cx - s * 0.1), Math.round(s * 0.5), Math.round(s * 0.28), lo)
  a.vline(Math.round(cx + s * 0.09), Math.round(s * 0.5), Math.round(s * 0.28), lo)
  // shoulders
  a.ellipse(cx - s * 0.25, s * 0.48, s * 0.11, s * 0.09, hi)
  a.ellipse(cx + s * 0.25, s * 0.48, s * 0.11, s * 0.09, hi)
  // ember sash
  a.hline(Math.round(cx - s * 0.26), Math.round(s * 0.58), Math.round(s * 0.52), trim)
  a.hline(Math.round(cx - s * 0.22), Math.round(s * 0.59), Math.round(s * 0.44), shade(trim, -0.35))

  // hood: pointed, unmistakable from above
  a.ellipse(cx, s * 0.3, s * 0.23, s * 0.22, base)
  a.ellipse(cx, s * 0.22, s * 0.13, s * 0.12, hi)
  a.ellipse(cx, s * 0.34, s * 0.16, s * 0.13, '#171423')
  // two ember eyes, 2px each so they read at a glance
  a.rect(Math.round(cx - s * 0.11), Math.round(s * 0.33), 2, 2, ember)
  a.rect(Math.round(cx + s * 0.04), Math.round(s * 0.33), 2, 2, ember)
  a.rect(Math.round(cx - s * 0.11), Math.round(s * 0.33), 1, 1, '#fff2cf')
  a.rect(Math.round(cx + s * 0.04), Math.round(s * 0.33), 1, 1, '#fff2cf')

  // weapon, held out to the Warden's left (screen right)
  const weapon = pstr(p, 'weapon', 'blade')
  const wx = Math.round(cx + s * 0.33)
  if (weapon === 'bow') {
    const wood = '#9a6537'
    a.vline(wx, Math.round(s * 0.26), Math.round(s * 0.5), wood)
    a.set(wx - 1, Math.round(s * 0.3), wood)
    a.set(wx - 1, Math.round(s * 0.72), wood)
    a.vline(wx - 2, Math.round(s * 0.3), Math.round(s * 0.44), '#efe4c6')
  } else if (weapon === 'staff') {
    a.vline(wx, Math.round(s * 0.24), Math.round(s * 0.6), '#7a5432')
    a.disc(wx, Math.round(s * 0.2), 2.6, ember)
  } else if (weapon === 'maul') {
    a.vline(wx, Math.round(s * 0.3), Math.round(s * 0.54), '#7a5432')
    a.rect(wx - 3, Math.round(s * 0.2), 7, 6, '#9aa2b4')
    a.rect(wx - 3, Math.round(s * 0.2), 7, 2, '#c2c9d8')
  } else {
    // cinder blade: pale steel with a live ember edge
    a.vline(wx, Math.round(s * 0.16), Math.round(s * 0.46), '#aeb6c9')
    a.vline(wx + 1, Math.round(s * 0.18), Math.round(s * 0.44), ember)
    a.set(wx, Math.round(s * 0.15), '#fff2cf')
    a.hline(wx - 2, Math.round(s * 0.63), 5, '#7a5432')
    a.vline(wx, Math.round(s * 0.64), 3, '#7a5432')
  }
  skinTouch(a, cx, s, '#c9b394')
})

function skinTouch(a: PixelArt, cx: number, s: number, skin: string): void {
  // gloved hands peeking from the cloak
  a.set(Math.round(cx + s * 0.28), Math.round(s * 0.62), skin)
  a.set(Math.round(cx - s * 0.28), Math.round(s * 0.62), skin)
}

// --- zone 1 roster ----------------------------------------------------------

registerPainter('gnawling', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const fur = pstr(p, 'color', '#8a6f4e')
  const dark = shade(fur, -0.42)
  const belly = shade(fur, 0.3)
  const eye = pstr(p, 'eye', pal.glow)

  // four legs first, so the body sits on top of them
  const legY = Math.round(s * 0.72)
  const legH = Math.max(3, Math.round(s * 0.18))
  a.rect(Math.round(cx - s * 0.32), legY, 3, legH, dark)
  a.rect(Math.round(cx - s * 0.13), legY + 1, 3, legH - 1, dark)
  a.rect(Math.round(cx + s * 0.09), legY + 1, 3, legH - 1, dark)
  a.rect(Math.round(cx + s * 0.28), legY, 3, legH, dark)
  // paws
  a.hline(Math.round(cx - s * 0.34), legY + legH, 4, dark)
  a.hline(Math.round(cx + s * 0.27), legY + legH, 4, dark)

  // haunches then body: wide, low, ground-hugging
  a.ellipse(cx - s * 0.2, s * 0.58, s * 0.17, s * 0.15, dark)
  a.ellipse(cx + s * 0.2, s * 0.58, s * 0.17, s * 0.15, dark)
  a.ellipse(cx, s * 0.58, s * 0.33, s * 0.2, fur)
  a.ellipse(cx, s * 0.5, s * 0.28, s * 0.09, dark) // spine ridge
  a.ellipse(cx, s * 0.66, s * 0.2, s * 0.09, belly)

  // tail, thick at the root
  a.line(
    Math.round(cx + s * 0.26),
    Math.round(s * 0.66),
    Math.round(cx + s * 0.46),
    Math.round(s * 0.84),
    dark,
  )
  a.set(Math.round(cx + s * 0.3), Math.round(s * 0.7), dark)

  // head thrust forward and low
  a.ellipse(cx, s * 0.32, s * 0.2, s * 0.17, fur)
  // ears
  a.ellipse(cx - s * 0.17, s * 0.2, s * 0.06, s * 0.08, dark)
  a.ellipse(cx + s * 0.17, s * 0.2, s * 0.06, s * 0.08, dark)
  a.ellipse(cx - s * 0.17, s * 0.21, s * 0.03, s * 0.04, belly)
  a.ellipse(cx + s * 0.17, s * 0.21, s * 0.03, s * 0.04, belly)
  // snout wedge + nose
  a.ellipse(cx, s * 0.19, s * 0.1, s * 0.09, belly)
  a.rect(Math.round(cx - 1), Math.round(s * 0.12), 2, 2, '#2a2029')
  // eyes: two bright pips, the readability anchor
  a.rect(Math.round(cx - s * 0.13), Math.round(s * 0.3), 2, 2, eye)
  a.rect(Math.round(cx + s * 0.05), Math.round(s * 0.3), 2, 2, eye)
  // teeth
  a.set(Math.round(cx - s * 0.06), Math.round(s * 0.24), '#efe8d4')
  a.set(Math.round(cx + s * 0.05), Math.round(s * 0.24), '#efe8d4')
})

registerPainter('husk', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const iron = pstr(p, 'color', '#5b6472')
  const ironLo = shade(iron, -0.35)
  const ironHi = shade(iron, 0.22)
  const cloth = pstr(p, 'cloth', '#4a3a2e')
  const eye = pstr(p, 'eye', pal.glow)

  // upright, narrow, top-heavy — the opposite silhouette to the gnawling
  a.rect(Math.round(cx - s * 0.09), Math.round(s * 0.68), 3, Math.round(s * 0.24), ironLo) // legs
  a.rect(Math.round(cx + s * 0.03), Math.round(s * 0.68), 3, Math.round(s * 0.24), ironLo)
  a.rect(Math.round(cx - s * 0.2), Math.round(s * 0.86), Math.round(s * 0.16), 2, ironLo)
  a.rect(Math.round(cx + s * 0.04), Math.round(s * 0.86), Math.round(s * 0.16), 2, ironLo)
  // tabard / skirt
  a.rect(Math.round(cx - s * 0.17), Math.round(s * 0.58), Math.round(s * 0.34), Math.round(s * 0.16), cloth)
  // cuirass
  a.rect(Math.round(cx - s * 0.22), Math.round(s * 0.36), Math.round(s * 0.44), Math.round(s * 0.24), iron)
  a.hline(Math.round(cx - s * 0.22), Math.round(s * 0.36), Math.round(s * 0.44), ironHi)
  a.vline(Math.round(cx), Math.round(s * 0.38), Math.round(s * 0.2), ironLo)
  // pauldrons
  a.ellipse(cx - s * 0.27, s * 0.4, s * 0.11, s * 0.08, ironHi)
  a.ellipse(cx + s * 0.27, s * 0.4, s * 0.11, s * 0.08, ironHi)
  // helm with a slit
  a.ellipse(cx, s * 0.22, s * 0.16, s * 0.16, iron)
  a.rect(Math.round(cx - s * 0.12), Math.round(s * 0.22), Math.round(s * 0.24), 2, '#0e0d14')
  a.set(Math.round(cx - s * 0.07), Math.round(s * 0.22), eye)
  a.set(Math.round(cx + s * 0.06), Math.round(s * 0.22), eye)
  a.vline(Math.round(cx), Math.round(s * 0.1), Math.round(s * 0.14), ironHi)
  // shortsword, point down, ready to poke
  const wx = Math.round(cx + s * 0.36)
  a.vline(wx, Math.round(s * 0.42), Math.round(s * 0.34), '#a9b0bf')
  a.hline(wx - 1, Math.round(s * 0.4), 3, '#6b4a2c')
})

registerPainter('bonecaster', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const robe = pstr(p, 'color', '#4b3f63')
  const robeLo = shade(robe, -0.35)
  const bone = '#ddd6c0'
  const glow = pstr(p, 'glow', '#9be36b')

  // tall cone robe — unmistakable "back rank caster"
  for (let y = 0; y < Math.round(s * 0.52); y++) {
    const t = y / (s * 0.52)
    const w = Math.round(s * (0.1 + 0.32 * t))
    a.hline(Math.round(cx - w), Math.round(s * 0.4 + y), w * 2, y % 5 === 4 ? robeLo : robe)
  }
  a.ellipse(cx, s * 0.9, s * 0.4, s * 0.06, robeLo)
  // hood + skull
  a.ellipse(cx, s * 0.28, s * 0.19, s * 0.2, robe)
  a.ellipse(cx, s * 0.3, s * 0.13, s * 0.13, bone)
  a.ellipse(cx, s * 0.36, s * 0.08, s * 0.06, shade(bone, -0.12))
  a.set(Math.round(cx - s * 0.06), Math.round(s * 0.3), '#141018')
  a.set(Math.round(cx + s * 0.05), Math.round(s * 0.3), '#141018')
  a.set(Math.round(cx - s * 0.06), Math.round(s * 0.31), glow)
  a.set(Math.round(cx + s * 0.05), Math.round(s * 0.31), glow)
  // staff with a charged bone knot
  const wx = Math.round(cx + s * 0.36)
  a.vline(wx, Math.round(s * 0.2), Math.round(s * 0.7), '#6b5236')
  a.disc(wx, Math.round(s * 0.16), 2.4, bone)
  a.set(wx, Math.round(s * 0.16), glow)
  a.set(wx - 3, Math.round(s * 0.14), glow)
  a.set(wx + 3, Math.round(s * 0.18), glow)
})

registerPainter('gloomspawn', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const body = pstr(p, 'color', '#2a1f3d')
  const edge = shade(body, 0.35)
  const eye = pstr(p, 'eye', '#c07bff')

  // jagged wisp: a torn blob with spikes, no legs, reads as "not solid"
  a.ellipse(cx, s * 0.5, s * 0.27, s * 0.3, body)
  const spikes = 7
  for (let i = 0; i < spikes; i++) {
    const ang = (i / spikes) * Math.PI * 2 + 0.4
    const r0 = s * 0.24
    const r1 = s * (0.4 + 0.08 * (i % 2))
    a.line(
      Math.round(cx + Math.cos(ang) * r0),
      Math.round(s * 0.5 + Math.sin(ang) * r0),
      Math.round(cx + Math.cos(ang) * r1),
      Math.round(s * 0.5 + Math.sin(ang) * r1),
      i % 2 ? body : edge,
    )
  }
  a.ellipse(cx, s * 0.46, s * 0.12, s * 0.1, '#0d0814')
  a.set(Math.round(cx - s * 0.07), Math.round(s * 0.46), eye)
  a.set(Math.round(cx + s * 0.06), Math.round(s * 0.46), eye)
  a.set(Math.round(cx - s * 0.07), Math.round(s * 0.47), eye)
  a.set(Math.round(cx + s * 0.06), Math.round(s * 0.47), eye)
})

// --- generators (three unmistakable shapes) ---------------------------------

registerPainter('gen_bonepile', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const bone = pstr(p, 'color', '#e2dac0')
  const boneLo = shade(bone, -0.34)
  const dirt = shade(pal.floor, -0.5)

  // spill of earth, then a heap of pale bone — high contrast against the roster
  a.ellipse(cx, s * 0.82, s * 0.46, s * 0.13, dirt)
  a.ellipse(cx, s * 0.68, s * 0.42, s * 0.21, boneLo)
  a.ellipse(cx, s * 0.63, s * 0.36, s * 0.17, bone)

  // ribs arcing out of the heap
  for (let i = -2; i <= 2; i++) {
    if (i === 0) continue
    const x = Math.round(cx + i * s * 0.15)
    a.line(x, Math.round(s * 0.62), Math.round(x + i * 3), Math.round(s * 0.34), bone)
    a.set(Math.round(x + i * 3), Math.round(s * 0.33), shade(bone, 0.25))
    a.set(Math.round(x + i * 2), Math.round(s * 0.45), boneLo)
  }
  // femur laid across the front
  a.line(
    Math.round(cx - s * 0.3),
    Math.round(s * 0.78),
    Math.round(cx + s * 0.22),
    Math.round(s * 0.74),
    bone,
  )
  a.disc(cx - s * 0.32, s * 0.79, 2.2, bone)
  a.disc(cx + s * 0.24, s * 0.74, 2.2, bone)

  // THE MAW — every generator carries this violet rift so "source" reads
  // instantly, whatever body the zone hangs on it.
  a.ellipse(cx, s * 0.6, s * 0.16, s * 0.12, '#150c22')
  a.ellipse(cx, s * 0.61, s * 0.11, s * 0.08, '#5b2f92')
  a.ellipse(cx, s * 0.62, s * 0.06, s * 0.04, '#a774e8')

  // skull crowning the heap
  a.ellipse(cx, s * 0.3, s * 0.15, s * 0.14, bone)
  a.ellipse(cx, s * 0.36, s * 0.09, s * 0.06, boneLo)
  a.rect(Math.round(cx - s * 0.09), Math.round(s * 0.29), 3, 3, '#150c22')
  a.rect(Math.round(cx + s * 0.03), Math.round(s * 0.29), 3, 3, '#150c22')
  a.hline(Math.round(cx - s * 0.06), Math.round(s * 0.41), Math.round(s * 0.12), '#150c22')
})

registerPainter('gen_barracks', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const wood = pstr(p, 'color', '#5a4126')
  const woodLo = shade(wood, -0.35)
  const iron = '#59606d'
  const dark = '#100d16'

  // a broken doorway in a frame — tall rectangle, hard edges
  a.rect(Math.round(cx - s * 0.36), Math.round(s * 0.12), Math.round(s * 0.72), Math.round(s * 0.8), woodLo)
  a.rect(Math.round(cx - s * 0.28), Math.round(s * 0.2), Math.round(s * 0.56), Math.round(s * 0.72), dark)
  // splintered planks hanging in the gap
  a.rect(Math.round(cx - s * 0.26), Math.round(s * 0.2), Math.round(s * 0.14), Math.round(s * 0.4), wood)
  a.rect(Math.round(cx + s * 0.1), Math.round(s * 0.2), Math.round(s * 0.12), Math.round(s * 0.28), wood)
  a.line(
    Math.round(cx - s * 0.26),
    Math.round(s * 0.6),
    Math.round(cx + s * 0.22),
    Math.round(s * 0.42),
    wood,
  )
  // THE MAW — shared "this is a source" read
  a.ellipse(cx, s * 0.56, s * 0.15, s * 0.19, '#150c22')
  a.ellipse(cx, s * 0.58, s * 0.1, s * 0.13, '#5b2f92')
  a.ellipse(cx, s * 0.6, s * 0.05, s * 0.07, '#a774e8')
  // iron bands + rivets
  a.hline(Math.round(cx - s * 0.36), Math.round(s * 0.16), Math.round(s * 0.72), iron)
  a.hline(Math.round(cx - s * 0.36), Math.round(s * 0.86), Math.round(s * 0.72), iron)
  a.set(Math.round(cx - s * 0.3), Math.round(s * 0.16), shade(iron, 0.3))
  a.set(Math.round(cx + s * 0.28), Math.round(s * 0.86), shade(iron, 0.3))
})

registerPainter('gen_brazier', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const stone = pstr(p, 'color', shade(pal.wall, 0.1))
  const stoneLo = shade(stone, -0.35)
  const flame = pstr(p, 'flame', pal.glow)

  // a standing altar-brazier: narrow column, flared bowl, flame on top
  a.rect(Math.round(cx - s * 0.24), Math.round(s * 0.84), Math.round(s * 0.48), Math.round(s * 0.1), stoneLo)
  a.rect(Math.round(cx - s * 0.12), Math.round(s * 0.5), Math.round(s * 0.24), Math.round(s * 0.36), stone)
  a.vline(Math.round(cx - s * 0.1), Math.round(s * 0.52), Math.round(s * 0.32), stoneLo)
  // bowl
  for (let i = 0; i < Math.round(s * 0.14); i++) {
    const w = Math.round(s * (0.42 - i * 0.02))
    a.hline(Math.round(cx - w / 2), Math.round(s * 0.38 + i), w, i < 2 ? shade(stone, 0.2) : stone)
  }
  // THE MAW — shared "this is a source" read, set into the column
  a.ellipse(cx, s * 0.64, s * 0.08, s * 0.1, '#150c22')
  a.ellipse(cx, s * 0.65, s * 0.05, s * 0.06, '#a774e8')
  // flame
  a.ellipse(cx, s * 0.3, s * 0.14, s * 0.16, flame)
  a.ellipse(cx, s * 0.34, s * 0.08, s * 0.1, shade(flame, 0.45))
  a.line(Math.round(cx), Math.round(s * 0.16), Math.round(cx - 1), Math.round(s * 0.26), flame)
  a.set(Math.round(cx + s * 0.14), Math.round(s * 0.22), flame)
  a.set(Math.round(cx - s * 0.16), Math.round(s * 0.24), flame)
})

// --- pickups ----------------------------------------------------------------

registerPainter('pickup_food', (a, _p, _pal) => {
  const s = a.size
  const cx = s / 2
  const meat = '#a8503c'
  const bone = '#e8e0c8'
  // drumstick
  a.ellipse(cx + s * 0.06, s * 0.4, s * 0.26, s * 0.24, meat)
  a.ellipse(cx + s * 0.02, s * 0.34, s * 0.12, s * 0.1, shade(meat, 0.25))
  a.line(
    Math.round(cx - s * 0.1),
    Math.round(s * 0.56),
    Math.round(cx - s * 0.3),
    Math.round(s * 0.82),
    bone,
  )
  a.disc(cx - s * 0.32, s * 0.84, 2, bone)
  a.disc(cx - s * 0.22, s * 0.86, 1.6, bone)
})

registerPainter('pickup_bread', (a, _p, _pal) => {
  const s = a.size
  const cx = s / 2
  const crust = '#b8813f'
  a.ellipse(cx, s * 0.55, s * 0.36, s * 0.24, crust)
  a.ellipse(cx, s * 0.48, s * 0.3, s * 0.14, shade(crust, 0.25))
  for (let i = -1; i <= 1; i++) {
    a.line(
      Math.round(cx + i * s * 0.16 - 2),
      Math.round(s * 0.42),
      Math.round(cx + i * s * 0.16 + 2),
      Math.round(s * 0.36),
      shade(crust, -0.3),
    )
  }
})

registerPainter('pickup_gold', (a, _p, pal) => {
  const s = a.size
  const cx = s / 2
  const gold = '#e3b23c'
  const goldHi = '#ffe08a'
  const goldLo = '#9c7420'
  a.ellipse(cx, s * 0.72, s * 0.4, s * 0.14, goldLo)
  a.ellipse(cx - s * 0.14, s * 0.6, s * 0.16, s * 0.1, gold)
  a.ellipse(cx + s * 0.16, s * 0.62, s * 0.15, s * 0.09, gold)
  a.ellipse(cx, s * 0.46, s * 0.17, s * 0.11, gold)
  a.ellipse(cx - s * 0.02, s * 0.43, s * 0.1, s * 0.05, goldHi)
  a.set(Math.round(cx + s * 0.2), Math.round(s * 0.36), goldHi)
})

registerPainter('pickup_key', (a, _p, pal) => {
  const s = a.size
  const cx = s / 2
  const metal = '#e6d68a'
  const metalLo = shade(metal, -0.35)
  a.ring(cx, s * 0.28, s * 0.16, metal)
  a.ring(cx, s * 0.28, s * 0.16 - 1, metalLo)
  a.vline(Math.round(cx), Math.round(s * 0.44), Math.round(s * 0.42), metal)
  a.hline(Math.round(cx), Math.round(s * 0.72), Math.round(s * 0.2), metal)
  a.hline(Math.round(cx), Math.round(s * 0.82), Math.round(s * 0.14), metal)
})

registerPainter('pickup_potion', (a, _p, pal) => {
  const s = a.size
  const cx = s / 2
  const glass = '#7fd0e8'
  const fluid = pstr(_p, 'fluid', '#ff8a3c')
  a.rect(Math.round(cx - 2), Math.round(s * 0.16), 4, Math.round(s * 0.16), '#6b4a2c')
  a.ellipse(cx, s * 0.62, s * 0.28, s * 0.26, glass)
  a.ellipse(cx, s * 0.66, s * 0.22, s * 0.2, fluid)
  a.ellipse(cx - s * 0.08, s * 0.54, s * 0.06, s * 0.08, '#ffffff')
  a.hline(Math.round(cx - s * 0.2), Math.round(s * 0.56), Math.round(s * 0.4), shade(fluid, 0.4))
})

registerPainter('pickup_relic', (a, _p, pal) => {
  const s = a.size
  const cx = s / 2
  const glow = pstr(_p, 'color', pal.glow)
  a.ellipse(cx, s * 0.5, s * 0.24, s * 0.3, glow)
  a.ellipse(cx, s * 0.5, s * 0.14, s * 0.2, shade(glow, 0.5))
  a.ring(cx, s * 0.5, s * 0.36, shade(glow, -0.3))
})

// --- doors ------------------------------------------------------------------

registerPainter('door_closed', (a, p, pal) => {
  const s = a.size
  const iron = pstr(p, 'color', '#4a4a58')
  const ironLo = shade(iron, -0.4)
  const ironHi = shade(iron, 0.25)
  a.rect(0, 0, s, s, ironLo)
  a.rect(2, 1, s - 4, s - 2, iron)
  for (let x = 4; x < s - 3; x += 7) a.vline(x, 2, s - 4, ironLo)
  a.hline(2, Math.round(s * 0.25), s - 4, ironHi)
  a.hline(2, Math.round(s * 0.72), s - 4, ironHi)
  a.disc(s * 0.5, s * 0.5, s * 0.1, ironHi)
  a.disc(s * 0.5, s * 0.5, s * 0.05, ironLo)
})

registerPainter('door_open', (a, p, pal) => {
  const s = a.size
  const dark = '#0a0810'
  const iron = pstr(p, 'color', '#4a4a58')
  a.rect(0, 0, s, s, dark)
  a.vline(0, 0, s, shade(iron, -0.3))
  a.vline(1, 0, s, shade(iron, -0.5))
  a.vline(s - 1, 0, s, shade(iron, -0.3))
  a.vline(s - 2, 0, s, shade(iron, -0.5))
  a.hline(0, 0, s, shade(iron, -0.2))
  // warm light spilling from beyond
  for (let i = 0; i < 5; i++) {
    a.hline(3, s - 2 - i, s - 6, pal.glow, 0.06 * (5 - i))
  }
})

registerPainter('gate_locked', (a, p, pal) => {
  const s = a.size
  const iron = pstr(p, 'color', '#6a5c3a')
  const ironLo = shade(iron, -0.4)
  a.rect(0, 0, s, s, '#0a0810')
  for (let x = 2; x < s - 1; x += 6) {
    a.vline(x, 0, s, iron)
    a.vline(x + 1, 0, s, ironLo)
  }
  a.hline(0, 3, s, iron)
  a.hline(0, s - 5, s, iron)
  a.disc(s * 0.5, s * 0.5, s * 0.12, iron)
  a.disc(s * 0.5, s * 0.5, s * 0.05, '#0a0810')
})

// --- projectiles ------------------------------------------------------------

registerPainter('arrow', (a, p, _pal) => {
  const s = a.size
  const cy = Math.round(s / 2)
  const shaftC = pstr(p, 'color', '#ffe9a8')
  a.hline(1, cy, s - 3, shaftC)
  a.hline(1, cy - 1, s - 5, shade(shaftC, -0.25))
  a.set(s - 2, cy, '#ffffff')
  a.set(s - 3, cy - 1, '#ffffff')
  a.set(s - 3, cy + 1, '#ffffff')
  a.set(1, cy - 1, shade(shaftC, -0.4))
  a.set(1, cy + 1, shade(shaftC, -0.4))
})

registerPainter('bonebolt', (a, p, _pal) => {
  const s = a.size
  const c = pstr(p, 'color', '#9be36b')
  a.disc(s / 2, s / 2, s * 0.34, c)
  a.disc(s / 2 - 1, s / 2 - 1, s * 0.18, shade(c, 0.5))
  a.set(1, Math.round(s / 2), shade(c, -0.3))
  a.set(s - 2, Math.round(s / 2), shade(c, -0.3))
})

registerPainter('stormbolt', (a, p, _pal) => {
  const s = a.size
  const c = pstr(p, 'color', '#8fd8ff')
  const hot = shade(c, 0.55)
  // a fat, slow ball of weather with a bright core and a ragged tail
  a.disc(s * 0.56, s / 2, s * 0.3, c)
  a.disc(s * 0.54, s / 2 - 1, s * 0.16, hot)
  a.set(Math.round(s * 0.54), Math.round(s / 2 - 1), '#ffffff')
  a.hline(1, Math.round(s / 2), Math.round(s * 0.28), shade(c, -0.2))
  a.set(2, Math.round(s / 2 - 1), shade(c, -0.4))
  a.set(2, Math.round(s / 2 + 1), shade(c, -0.4))
})

registerPainter('broodbolt', (a, p, _pal) => {
  const s = a.size
  const c = pstr(p, 'color', '#ffd98a')
  const lo = shade(c, -0.4)
  // a bone shard, tapered, so the Heart's spread never reads as your own arrow
  a.ellipse(s / 2, s / 2, s * 0.34, s * 0.17, c)
  a.hline(Math.round(s * 0.2), Math.round(s / 2), Math.round(s * 0.6), shade(c, 0.4))
  a.set(Math.round(s * 0.86), Math.round(s / 2), '#ffffff')
  a.set(Math.round(s * 0.14), Math.round(s / 2 - 1), lo)
  a.set(Math.round(s * 0.14), Math.round(s / 2 + 1), lo)
})

// --- boss 1: the Broodgate ---------------------------------------------------
// Bone and mortar. The maw is a mouth in a bone ring (closed = teeth locked,
// open = a black throat); the Heart is a bound organ that plates itself over
// while its network stands, so "sealed" is a silhouette, not a tint.

registerPainter('broodgate_maw', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const cy = s / 2
  const bone = pstr(p, 'bone', '#d8cfb4')
  const dark = shade(bone, -0.45)
  const maw = pstr(p, 'maw', '#2b1a24')
  const open = pget(p, 'open', 0) > 0

  // socket in the wall
  a.disc(cx, cy, s * 0.46, shade(bone, -0.62))
  a.disc(cx, cy, s * 0.42, bone)
  a.disc(cx, cy - 1, s * 0.34, shade(bone, 0.16))
  // the throat
  a.ellipse(cx, cy + (open ? 1 : 0), s * (open ? 0.3 : 0.26), s * (open ? 0.26 : 0.1), maw)
  if (open) {
    a.ellipse(cx, cy + 3, s * 0.2, s * 0.13, '#5a2340')
    a.ellipse(cx, cy + 4, s * 0.1, s * 0.07, pal.glow)
  }
  // teeth around the rim
  const teeth = 9
  for (let i = 0; i < teeth; i++) {
    const ang = (i / teeth) * Math.PI * 2
    const r = s * (open ? 0.29 : 0.24)
    const tx = Math.round(cx + Math.cos(ang) * r)
    const ty = Math.round(cy + Math.sin(ang) * r * 0.9)
    a.set(tx, ty, bone)
    a.set(tx, ty + 1, shade(bone, 0.35))
  }
  // ribs of the surround
  a.vline(Math.round(cx - s * 0.42), Math.round(cy - s * 0.16), Math.round(s * 0.32), dark)
  a.vline(Math.round(cx + s * 0.41), Math.round(cy - s * 0.16), Math.round(s * 0.32), dark)
})

registerPainter('broodgate_heart', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const cy = s / 2
  const sealed = pget(p, 'sealed', 0) > 0
  const flesh = sealed ? '#5c4652' : '#8e2f38'
  const hot = sealed ? '#6d5462' : '#e0523c'

  // the cage of bone the gate grew around it
  a.disc(cx, cy, s * 0.46, '#2a2028')
  a.disc(cx, cy, s * 0.42, '#3a2c33')
  // the organ
  a.ellipse(cx, cy + 1, s * 0.3, s * 0.32, flesh)
  a.ellipse(cx - s * 0.09, cy - s * 0.06, s * 0.13, s * 0.14, shade(flesh, 0.28))
  a.ellipse(cx, cy + s * 0.14, s * 0.2, s * 0.14, shade(flesh, -0.3))
  // vessels
  a.line(cx - s * 0.22, cy - s * 0.2, cx + s * 0.02, cy + s * 0.16, shade(flesh, -0.45))
  a.line(cx + s * 0.2, cy - s * 0.18, cx - s * 0.02, cy + s * 0.2, shade(flesh, -0.45))
  if (!sealed) {
    a.ellipse(cx, cy, s * 0.1, s * 0.11, hot)
    a.ellipse(cx, cy, s * 0.05, s * 0.05, pal.glow)
  } else {
    // plates bolted over it — the silhouette says "do not bother"
    const plate = '#8d97a8'
    a.rect(Math.round(cx - s * 0.4), Math.round(cy - s * 0.14), Math.round(s * 0.8), Math.round(s * 0.1), plate)
    a.rect(Math.round(cx - s * 0.4), Math.round(cy - s * 0.14), Math.round(s * 0.8), 2, shade(plate, 0.3))
    a.rect(Math.round(cx - s * 0.4), Math.round(cy + s * 0.08), Math.round(s * 0.8), Math.round(s * 0.1), plate)
    a.rect(Math.round(cx - s * 0.4), Math.round(cy + s * 0.08), Math.round(s * 0.8), 2, shade(plate, 0.3))
    a.set(Math.round(cx - s * 0.3), Math.round(cy - s * 0.1), '#3a3f4a')
    a.set(Math.round(cx + s * 0.29), Math.round(cy - s * 0.1), '#3a3f4a')
    a.set(Math.round(cx - s * 0.3), Math.round(cy + s * 0.12), '#3a3f4a')
    a.set(Math.round(cx + s * 0.29), Math.round(cy + s * 0.12), '#3a3f4a')
  }
})

// ---------------------------------------------------------------- core recipes
// Instances every zone needs. Zone data registers its own bodies on top; a zone
// that wants a different door or floor simply re-registers the same id.

registerRecipe('tile_floor', { key: 'tile_floor', size: 32 })
registerRecipe('tile_floor_alt', { key: 'tile_floor_alt', size: 32 })
registerRecipe('tile_wall', { key: 'tile_wall', size: 32 })

registerRecipe('warden_cinderblade', { key: 'warden', size: 30, params: { weapon: 'blade' } })
registerRecipe('warden_whisperbow', { key: 'warden', size: 30, params: { weapon: 'bow' } })
registerRecipe('warden_stormstaff', { key: 'warden', size: 30, params: { weapon: 'staff' } })
registerRecipe('warden_bulwarkmaul', { key: 'warden', size: 30, params: { weapon: 'maul' } })

registerRecipe('pickup_food', { key: 'pickup_food', size: 19 })
registerRecipe('pickup_bread', { key: 'pickup_bread', size: 19 })
registerRecipe('pickup_gold', { key: 'pickup_gold', size: 19 })
registerRecipe('pickup_key', { key: 'pickup_key', size: 19 })
registerRecipe('pickup_potion', { key: 'pickup_potion', size: 20 })
registerRecipe('pickup_relic', { key: 'pickup_relic', size: 20 })

registerRecipe('door_closed', { key: 'door_closed', size: 32 })
registerRecipe('door_open', { key: 'door_open', size: 32 })
registerRecipe('gate_locked', { key: 'gate_locked', size: 32 })
